import { isRuntimeFlagEnabled } from '~/utils/runtimeFlag';
import {
  CALLTOUCH_MOD_ID_PATTERN,
  CALLTOUCH_SESSION_STORAGE_KEY,
  inspectCalltouchSession,
  readCalltouchCookieSessionId,
  waitForCalltouchSession,
  type CalltouchDiagnosticStatus,
  type CalltouchLoaderStatus,
  type CalltouchSessionResult,
} from '~/utils/calltouch';

const BACKGROUND_TIMEOUT_MS = 15_000;
const CHECKOUT_TIMEOUT_MS = 1_500;

let initializationPromise: Promise<CalltouchSessionResult> | null = null;

function getLoaderStatus(): CalltouchLoaderStatus {
  if (typeof window === 'undefined') return 'not_started';

  return window.__wtfishCalltouchLoader?.status ?? 'not_started';
}

function getLoaderAttempts(): number {
  if (typeof window === 'undefined') return 0;

  return window.__wtfishCalltouchLoader?.attempts ?? 0;
}

function makeUnavailableResult(status: CalltouchDiagnosticStatus): CalltouchSessionResult {
  const ct = typeof window === 'undefined' ? undefined : window.ct;

  return {
    sessionId: null,
    diagnostics: {
      status,
      source: 'none',
      waited_ms: 0,
      loader_status: getLoaderStatus(),
      loader_attempts: getLoaderAttempts(),
      ct_present: Boolean(ct),
      ct_loaded: ct?.loaded === undefined ? null : ct.loaded,
    },
  };
}

function readCachedSessionId(): string | null {
  try {
    const sessionId = sessionStorage.getItem(CALLTOUCH_SESSION_STORAGE_KEY);
    return sessionId && /^\d+$/.test(sessionId) ? sessionId : null;
  } catch {
    return null;
  }
}

function readSavedCalltouchSessionId(): string | null {
  try {
    return readCalltouchCookieSessionId(document.cookie);
  } catch {
    return null;
  }
}

function persistSessionId(sessionId: string): void {
  try {
    sessionStorage.setItem(CALLTOUCH_SESSION_STORAGE_KEY, sessionId);
  } catch {
    // Storage restrictions must not affect checkout.
  }
}

function cacheSuccessfulResult(result: CalltouchSessionResult): CalltouchSessionResult {
  if (result.sessionId) persistSessionId(result.sessionId);

  return result;
}

function resolveConfiguration(): { enabled: boolean; modId: string } {
  const { isCalltouchEnabled, calltouchModId } = useRuntimeConfig().public;

  return {
    enabled: isRuntimeFlagEnabled(isCalltouchEnabled),
    modId: String(calltouchModId || ''),
  };
}

export function useCalltouch() {
  function initializeCalltouchSession(): Promise<CalltouchSessionResult> {
    if (!import.meta.client) return Promise.resolve(makeUnavailableResult('script_unavailable'));

    const { enabled, modId } = resolveConfiguration();
    if (!enabled) return Promise.resolve(makeUnavailableResult('disabled'));
    if (!CALLTOUCH_MOD_ID_PATTERN.test(modId)) return Promise.resolve(makeUnavailableResult('invalid_mod_id'));

    initializationPromise ??= waitForCalltouchSession(
      () => window.ct,
      modId,
      getLoaderStatus,
      BACKGROUND_TIMEOUT_MS,
      100,
      getLoaderAttempts,
    ).then(cacheSuccessfulResult);

    return initializationPromise;
  }

  async function getCalltouchSession(): Promise<CalltouchSessionResult> {
    if (!import.meta.client) return makeUnavailableResult('script_unavailable');

    const { enabled, modId } = resolveConfiguration();
    if (!enabled) return makeUnavailableResult('disabled');
    if (!CALLTOUCH_MOD_ID_PATTERN.test(modId)) return makeUnavailableResult('invalid_mod_id');

    const liveResult = inspectCalltouchSession(window.ct, modId, getLoaderStatus(), 0, getLoaderAttempts());
    if (liveResult.sessionId) return cacheSuccessfulResult(liveResult);

    const waitedResult = await waitForCalltouchSession(
      () => window.ct,
      modId,
      getLoaderStatus,
      CHECKOUT_TIMEOUT_MS,
      50,
      getLoaderAttempts,
    );
    if (waitedResult.sessionId) return cacheSuccessfulResult(waitedResult);

    const cachedSessionId = readCachedSessionId();
    if (cachedSessionId) {
      return {
        sessionId: cachedSessionId,
        diagnostics: {
          ...waitedResult.diagnostics,
          status: 'cached',
          source: 'session_storage',
        },
      };
    }

    const savedSessionId = readSavedCalltouchSessionId();
    if (savedSessionId) {
      persistSessionId(savedSessionId);

      return {
        sessionId: savedSessionId,
        diagnostics: {
          ...waitedResult.diagnostics,
          status: 'cached',
          source: 'calltouch_cookie',
        },
      };
    }

    return waitedResult;
  }

  async function getCalltouchSessionId(): Promise<string | null> {
    return (await getCalltouchSession()).sessionId;
  }

  return { getCalltouchSession, getCalltouchSessionId, initializeCalltouchSession };
}
